import type {
	PaletteColor,
	PaletteColorOptions,
	Theme,
} from '@mui/material/styles';
import type { PaletteMode, ThemeOptions } from '@mui/material';
import { createTheme } from '@mui/material';
import { atom } from 'jotai';
import {
	type BaseChartOptions,
	type ChartTabularData,
	ChartTheme,
} from '@carbon/charts';

const fontFamily = 'Heebo, "Helvetica Neue", Arial, sans-serif';

// TODO: use OS preferred color scheme (dark mode support)
// window.matchMedia('(prefers-color-scheme: dark)').matches
// 	? 'dark'
// 	: 'light'
export const themeModeAtom = atom<PaletteMode>('light');

const typography: Readonly<Partial<ThemeOptions['typography']>> = {
	fontFamily,
	h1: {
		fontWeight: 100,
	},
};

const components: Readonly<Partial<ThemeOptions['components']>> = {
	MuiAppBar: {
		defaultProps: {
			color: 'inherit',
		},
		styleOverrides: {
			colorInherit: {
				backgroundColor: '#fff',
			},
		},
	},
	MuiDialog: {
		defaultProps: {
			slotProps: {
				backdrop: {
					style: {
						backgroundColor: 'rgba(73, 80, 87, 0.7)',
					},
				},
			},
		},
	},
};

const paletteMixin: Readonly<Partial<ThemeOptions['palette']>> = {
	error: {
		main: '#E73B3B', // Flex core error color
	},
	warning: {
		main: '#f0ad4e',
	},
	darkGray: {
		main: '#495057',
	},
	lightGray: {
		main: '#76808B',
	},
	textGray: {
		main: '#343a40',
	},
	softGray: {
		main: '#F5F5F5',
	},
	disableGray: {
		main: '#D3D8DC',
	},
	navyBlue: {
		main: '#3A5DA0',
	},
};

const palette: Record<PaletteMode, Readonly<Partial<ThemeOptions>>> = {
	dark: {
		palette: {
			...paletteMixin,
			mode: 'dark',
			background: {
				paper: '#282c34',
				default: '#21252b',
			},
			primary: {
				main: '#55b9ef',
			},
			secondary: {
				main: '#1d4692',
			},
		},
	},
	light: {
		palette: {
			...paletteMixin,
			mode: 'light',
			primary: {
				main: '#048dd6',
			},
			background: {
				paper: '#fff',
				default: '#fafafa',
			},
			secondary: {
				main: '#1d4692',
			},
		},
	},
};

export const createFlexTheme = (mode: PaletteMode = 'light'): Theme => {
	return createTheme({
		typography,
		components,
		...palette[mode],
	});
};

export const flexTheme = createFlexTheme();

export const themeSelector = atom(get => {
	const mode = get(themeModeAtom);
	return createFlexTheme(mode);
});

export const chartThemeSelector = atom(get => {
	const mode = get(themeModeAtom);
	return 'dark' === mode ? ChartTheme.G90 : ChartTheme.WHITE;
});

const chartPalettes = {
	dark: [
		'#33b1ff', // Cyan 40
		'#007d79', // Teal 60
		'#ff7eb6', // Magenta 40
		'#fa4d56', // Red 50
		'#fff1f1', // Red 10
		'#6fdc8c', // Green 30
		'#4589ff', // Blue 50
		'#d12771', // Magenta 60
		'#d2a106', // Yellow 40
		'#08bdba', // Teal 40
		'#bae6ff', // Cyan 20
		'#ba4e00', // Orange 60
		'#d4bbff', // Purple 30
		'#8a3ffc', // Purple 60
	],
	light: [
		'#1192e8', // Cyan 50
		'#005d5d', // Teal 70
		'#9f1853', // Magenta 70
		'#fa4d56', // Red 50
		'#570408', // Red 90
		'#198038', // Green 60
		'#002d9c', // Blue 80
		'#ee538b', // Magenta 50
		'#b28600', // Yellow 50
		'#009d9a', // Teal 50
		'#012749', // Cyan 90
		'#8a3800', // Orange 70
		'#a56eff', // Purple 50
		'#6929c4', // Purple 70
	],
};

export const chartColorsSelector = atom(get => {
	const mode = get(themeModeAtom);
	return chartPalettes[mode];
});

export const chartColorsGeneratorFactorySelector = atom(get => {
	const palette = get(chartColorsSelector);

	return function* (): Generator<string, never> {
		let index = 0;
		while (true) {
			yield palette[index++ % palette.length];
		}
	};
});

type GetStrokeColorFunction = BaseChartOptions['getStrokeColor'] & object;

export const getStrokeColorFactorySelector = atom(
	get =>
		(data: ChartTabularData, groupKey = 'group'): GetStrokeColorFunction => {
			const chartColorsGenerator = get(chartColorsGeneratorFactorySelector)();
			const strokeColorsByGroup = new Map<string, string>();

			for (const row of data) {
				const group = row[groupKey];

				if (!strokeColorsByGroup.has(group)) {
					strokeColorsByGroup.set(group, chartColorsGenerator.next().value);
				}
			}

			return (group): string => strokeColorsByGroup.get(group) ?? '#000000';
		}
);

declare module '@mui/material/styles' {
	interface Palette {
		darkGray: PaletteColor;
		lightGray: PaletteColor;
		textGray: PaletteColor;
		softGray: PaletteColor;
		disableGray: PaletteColor;
		navyBlue: PaletteColor;
	}
	interface PaletteOptions {
		darkGray?: PaletteColorOptions;
		lightGray?: PaletteColorOptions;
		textGray?: PaletteColorOptions;
		softGray?: PaletteColorOptions;
		disableGray?: PaletteColorOptions;
		navyBlue?: PaletteColorOptions;
	}
}
